import React, { useState, useRef, useEffect } from "react";
import StoryCard from "../StoryCard/StoryCard.js";
import Slider from "react-slick";
import CTA from "@/components/common/CTA.js";
import Srcset from "@/components/common/Srcset";
import useMediaQuery from "@/helpers/hooks/useMediaQuery";
import { selectSliderElementsGen } from "@/helpers/generalHelper";
import { useIntersection } from "@/components/common/UseIntersection";
import { reportError } from "@/helpers/errorHelper";

function NextArrow(props) {
  const { className, onClick, textColor, refCallBack } = props;
  return (
    <div className={className} onClick={onClick} tabIndex={0} ref={refCallBack}>
      <Srcset
        requirePath={true}
        svgFile={true}
        src={`${textColor?"images/Icons/Chevron/large-white.svg":"images/Icons/Chevron/large.svg"}`}
        alt="Arrow Button"
        height="12"
        width="12"
      />
    </div>
  );
}

function PrevArrow(props) {
  const { className, onClick,textColor, refCallBack } = props;
  return (
    <div className={className} onClick={onClick} tabIndex={0} ref={refCallBack}>
      <Srcset
        requirePath={true}
        svgFile={true}
        src={`${textColor?"images/Icons/Chevron/large-white.svg":"images/Icons/Chevron/large.svg"}`}
        alt="Arrow Button"
        height="12"
        width="12"
      />
    </div>
  );
}

export default function ContentRow(props) {
  const isTablet = useMediaQuery("(max-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 600px)");

  let stateInitial = {
    activeSlide: 1,
  };
  const [state, setState] = useState(stateInitial);
  const nextArrowRef = useRef(null);
  const prevArrowRef = useRef(null);

  const handleArrowFocus = () => {
    const navbarHeight = 60;
    const focusedElement = document.activeElement;

    if(focusedElement === nextArrowRef?.current ){
      if(!inViewportNext || (nextArrowRef.current.getBoundingClientRect().top < navbarHeight)){
        focusedElement.scrollIntoView({behavior: "smooth", block: "center"});
      }
    }
    if (focusedElement === prevArrowRef?.current){
      if(!inViewportPrev || (prevArrowRef.current.getBoundingClientRect().top < navbarHeight)){
        focusedElement.scrollIntoView({behavior: "smooth", block: "center"});
      }
    }
  }

  let settings_3 = {
    dots: false,
    infinite: props.contentCards != "undefined" && props.contentCards && props.contentCards.length > 3,  
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 4,
    arrows: true,
    autoplay: false,
    lazyLoad: true,
    draggable: false,
    afterChange: (activeSlide) => setState({ activeSlide: Math.ceil(activeSlide / 4) + 1}),
    nextArrow: !isTablet && <NextArrow textColor={props.textColor} refCallBack={nextArrowRef} />,
    prevArrow: !isTablet && <PrevArrow textColor={props.textColor} refCallBack={prevArrowRef} />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          infinite: props.contentCards != "undefined" && props.contentCards && props.contentCards.length > 2,  
          slidesToShow: 3,
          slidesToScroll: 3,
          arrows: false,
          draggable: true,
          afterChange: (activeSlide) =>
            setState({ activeSlide: Math.ceil(activeSlide / 3) + 1}),
        },
      },
      {
        breakpoint: 600,
        settings: {
          infinite: true,
          slidesToShow: 1,
          slidesToScroll: 1,
          initialSlide: 2,
          centerMode: true,
          arrows: false,
          draggable: true,
          afterChange: (activeSlide) =>
            setState({ activeSlide: Math.ceil(activeSlide) + 1 }),
        },
      },
    ],
  };

  const sliderRef= useRef(null); 
  const inViewportNext = useIntersection(nextArrowRef, "0px");
  const inViewportPrev = useIntersection(prevArrowRef, "0px");
  const [tabPressed, setTabPressed] = useState(false);
  const isMobileDevice = useMediaQuery("(max-width: 767px)");
  useEffect(()=>{
    if (isMobileDevice) return; // Exit early if isMobileDevice is true
    const handleKeyDown = (event) =>{
      try {
        if(event?.key === "Tab" && !tabPressed){
          setTabPressed(true);
          selectSliderElementsGen(sliderRef);
        }
      } catch (error) {
        reportError("tab indexing error contentrow & not tab pressed");
        reportError(error) // Log error using reportError function
      }
    };
    handleArrowFocus();
    const handleKeyUp = (event) =>{
      try {
        if(event?.key === "Tab" ){
          setTabPressed(false);
        }
      } catch (error) {
        reportError("tab indexing error contentrow");
        reportError(error) // Log error using reportError function
      }
    };

    window.addEventListener("keydown",handleKeyDown);
    window.addEventListener("keyup",handleKeyUp);

    return ()=>{
      window.removeEventListener("keydown",handleKeyDown);
      window.removeEventListener("keyup",handleKeyUp);
    }
  },[sliderRef,tabPressed, isMobileDevice]);

  let slideCount = Math.ceil(props.contentCards && props.contentCards.length / 4) ;
  if(isMobile) {
    slideCount = Math.ceil(props.contentCards && props.contentCards.length);
  }
  else if(isTablet) { 
    slideCount = Math.ceil(props.contentCards && props.contentCards.length / 3);
  }
  return (
    <>
      <div
        className={`${"contentrow-section bg-" + props.backgroundColor.toLowerCase()} ${props.zoomEffect ? 'zoom_img' : ''} ${props.textColor && "text-light"}
        `}
      >
        <div className="top-header-sec text-center">
          <h3 className={`contentrow-heading heading-350`}>
            {props.contentHeading}
          </h3>
          {props.contentCTAText && (
            <div className="text-center top-cta">
              <CTA text={props.contentCTAText} link={props.contentCTAUrl} lightText={props.textColor} sizeClass='bordered large-md x-small-sm x-small-xs' />
            </div>
          )}
        </div>
        {slideCount > 1 && (
          <div className="small-md small-sm small-xs italic slider-counter">
            <span className="active-slide">{state.activeSlide}</span>
            <span className="slash">/</span>
            <span className="total-slide">{slideCount}</span>
          </div>
        )}
        <Slider ref={sliderRef} {...settings_3}>
          {props.contentCards &&
            props.contentCards.map((storyCard, index) => (
              <StoryCard key={index} {...storyCard} />
            ))}
        </Slider>
      </div>
    </>
  );
}

